import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Download, ArrowRight, Clock, Gift, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import Cookies from "js-cookie";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
    const { productTag } = useParams();
    const [status, setStatus] = useState("checking");
    const [downloadToken, setDownloadToken] = useState(null);
    const [bumpDownloadToken, setBumpDownloadToken] = useState(null);
    const [bumpProductName, setBumpProductName] = useState(null);
    const [downloadInfo, setDownloadInfo] = useState(null);
    const [bumpDownloadInfo, setBumpDownloadInfo] = useState(null);
    const [attempts, setAttempts] = useState(0);

    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");

    const visitorId = Cookies.get("visitor_id");

    // Fire Facebook Pixel Purchase event immediately on page load
    useEffect(() => {
        if (typeof window.fbq === 'function') {
            window.fbq('track', 'Purchase');
        }
    }, []);

    // Fetch download info for a token
    const fetchDownloadInfo = async (token, isBump = false) => {
        try {
            const res = await axios.get(`${API}/download/${token}/info`);
            if (isBump) {
                setBumpDownloadInfo(res.data);
            } else {
                setDownloadInfo(res.data);
            }
        } catch (e) {
            console.error("Error fetching download info:", e);
        }
    };

    useEffect(() => {
        if (!sessionId || !productTag) {
            setStatus("error");
            return;
        }

        const pollStatus = async () => {
            try {
                const res = await axios.get(`${API}/product/${productTag}/checkout/status/${sessionId}`);
                
                if (res.data.payment_status === "paid") {
                    setStatus("success");
                    
                    // Set download tokens
                    if (res.data.download_token) {
                        setDownloadToken(res.data.download_token);
                        fetchDownloadInfo(res.data.download_token, false);
                    }
                    
                    // Handle bump product
                    if (res.data.bump_download_token) {
                        setBumpDownloadToken(res.data.bump_download_token);
                        setBumpProductName(res.data.bump_product_name);
                        fetchDownloadInfo(res.data.bump_download_token, true);
                        
                        // Track bump purchase
                        if (visitorId) {
                            await axios.post(`${API}/track/funnel`, {
                                product_tag: productTag,
                                visitor_id: visitorId,
                                event: "bump_purchased",
                                metadata: { bump_name: res.data.bump_product_name }
                            });
                        }
                    }
                    
                    if (visitorId) {
                        await axios.post(`${API}/track/funnel`, {
                            product_tag: productTag,
                            visitor_id: visitorId,
                            event: "checkout_complete"
                        });
                    }
                } else if (res.data.status === "expired") {
                    setStatus("expired");
                } else if (attempts < 10) {
                    setAttempts(prev => prev + 1);
                    setTimeout(pollStatus, 2000);
                } else {
                    setStatus("pending");
                }
            } catch (e) {
                console.error("Error checking status:", e);
                if (attempts < 5) {
                    setAttempts(prev => prev + 1);
                    setTimeout(pollStatus, 2000);
                } else {
                    setStatus("error");
                }
            }
        };

        pollStatus();
    }, [sessionId, attempts, visitorId, productTag]);

    const handleDownload = async (token, isBump = false) => {
        // Open download in new tab
        window.open(`${API}/download/${token}`, '_blank');
        
        // Refresh download info after a short delay
        setTimeout(() => {
            fetchDownloadInfo(token, isBump);
        }, 1000);
    };

    const formatExpirationDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-lg w-full text-center"
            >
                {status === "checking" && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        <h1 className="font-heading text-3xl font-bold mb-4">
                            Processing Your Order...
                        </h1>
                        <p className="text-muted-foreground">
                            Please wait while we confirm your payment.
                        </p>
                    </>
                )}

                {status === "success" && (
                    <>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className="w-24 h-24 mx-auto mb-8 bg-primary/20 flex items-center justify-center"
                        >
                            <CheckCircle className="w-12 h-12 text-primary" />
                        </motion.div>
                        <h1 className="font-heading text-3xl font-bold mb-4 gold-text">
                            Thank You!
                        </h1>
                        <p className="text-muted-foreground mb-8">
                            Your purchase was successful. You now have instant access to your {bumpDownloadToken ? "guides" : "guide"}.
                        </p>

                        <div className="space-y-4 mb-6">
                            {downloadToken && (
                                <div className="card-landing p-4">
                                    <Button 
                                        onClick={() => handleDownload(downloadToken, false)}
                                        className="btn-primary px-8 py-6 text-lg w-full"
                                        data-testid="download-button"
                                        disabled={downloadInfo && !downloadInfo.is_valid}
                                    >
                                        <Download className="mr-2 w-5 h-5" />
                                        Download Your Guide
                                    </Button>
                                    
                                    {downloadInfo && (
                                        <div className="mt-3 text-sm text-muted-foreground space-y-1">
                                            <div className="flex items-center justify-center gap-2">
                                                <span>{downloadInfo.downloads_remaining} of {downloadInfo.max_downloads} downloads remaining</span>
                                            </div>
                                            <div className="text-xs">
                                                Link expires: {formatExpirationDate(downloadInfo.expires_at)}
                                            </div>
                                            {!downloadInfo.is_valid && (
                                                <div className="flex items-center justify-center gap-2 text-destructive mt-2">
                                                    <AlertCircle className="w-4 h-4" />
                                                    <span>{downloadInfo.is_expired ? "Link expired" : "Download limit reached"}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {bumpDownloadToken && (
                                <div className="card-landing p-4 border-accent/30">
                                    <Button 
                                        onClick={() => handleDownload(bumpDownloadToken, true)}
                                        className="btn-secondary px-8 py-6 text-lg w-full"
                                        data-testid="bump-download-button"
                                        disabled={bumpDownloadInfo && !bumpDownloadInfo.is_valid}
                                    >
                                        <Gift className="mr-2 w-5 h-5" />
                                        Download {bumpProductName || "Bonus"}
                                    </Button>
                                    
                                    {bumpDownloadInfo && (
                                        <div className="mt-3 text-sm text-muted-foreground space-y-1">
                                            <div className="flex items-center justify-center gap-2">
                                                <span>{bumpDownloadInfo.downloads_remaining} of {bumpDownloadInfo.max_downloads} downloads remaining</span>
                                            </div>
                                            <div className="text-xs">
                                                Link expires: {formatExpirationDate(bumpDownloadInfo.expires_at)}
                                            </div>
                                            {!bumpDownloadInfo.is_valid && (
                                                <div className="flex items-center justify-center gap-2 text-destructive mt-2">
                                                    <AlertCircle className="w-4 h-4" />
                                                    <span>{bumpDownloadInfo.is_expired ? "Link expired" : "Download limit reached"}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <p className="text-sm text-muted-foreground">
                            Download links have also been sent to your email address.
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                            Need help? Contact support if your links expire or run out.
                        </p>
                    </>
                )}

                {status === "pending" && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-8 bg-accent/20 flex items-center justify-center">
                            <Clock className="w-10 h-10 text-accent" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold mb-4">
                            Payment Processing
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            Your payment is being processed. This usually takes a few moments.
                            Please check your email for the download link.
                        </p>
                        <Button 
                            onClick={() => window.location.reload()}
                            className="btn-secondary px-6 py-3"
                        >
                            Check Again
                        </Button>
                    </>
                )}

                {status === "expired" && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-8 bg-destructive/20 flex items-center justify-center">
                            <Clock className="w-10 h-10 text-destructive" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold mb-4">
                            Session Expired
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            Your payment session has expired. Please try again.
                        </p>
                        <a href={`/${productTag}`}>
                            <Button className="btn-primary px-6 py-3">
                                Return to Product
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </a>
                    </>
                )}

                {status === "error" && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-8 bg-destructive/20 flex items-center justify-center">
                            <span className="text-4xl">!</span>
                        </div>
                        <h1 className="font-heading text-3xl font-bold mb-4">
                            Something Went Wrong
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            We could not verify your payment. Please contact support if you were charged.
                        </p>
                        <a href={`/${productTag}`}>
                            <Button className="btn-primary px-6 py-3">
                                Return to Product
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </a>
                    </>
                )}
            </motion.div>
        </div>
    );
};

export default SuccessPage;
